import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { EventType, type AuthenticationResult } from '@azure/msal-browser';
import { msal, sitzungAbgelaufen, sitzungBeenden, sitzungMerken } from './anmeldung/msal.js';
import { wecken } from './api/klient.js';
import { liveStarten } from './api/live.js';
import { OHNE_ANMELDUNG } from './anmeldung/entwicklung.js';
import { App } from './App.js';
import { Toasts } from './bausteine/Toast.js';
import { Seitenaktionen } from './bausteine/Seitenaktion.js';
/* Der Drawer haengt jetzt im `Anlegeweg` — der bringt den Empfaenger mit, der
   hier bis zum 13.08.2026 fehlte und alle neun Formulare in „noch nicht
   gespeichert" laufen liess. */
import { Anlegeweg } from './bausteine/Anlegeweg.js';
import { Modulrahmen } from './bausteine/Modulrahmen.js';
import { Standrahmen } from './bausteine/Standrahmen.js';
import './stile/basis.css';
import './stile/huelle.css';
import './stile/bausteine.css';

// Die serverlose Datenbank pausiert nach einer Stunde. Der Weckruf geht
// sofort raus, waehrend die Anmeldung noch laeuft — bis der Nutzer die erste
// Liste sieht, ist sie wach.
wecken();

// Keine Anweisung auf oberster Ebene mit await: das Auslieferungsziel
// schliesst Browser ein, die das nicht koennen.
async function starten(): Promise<void> {
  // Im Entwicklungsmodus gibt es keine Anmeldung — MSAL bleibt aus, sonst
  // scheitert die Initialisierung an der fehlenden Registrierung.
  if (!OHNE_ANMELDUNG) {
    await anmeldungVorbereiten();
  }

  const wurzel = document.getElementById('wurzel');
  if (!wurzel) throw new Error('Wurzelelement fehlt.');

  createRoot(wurzel).render(
    <StrictMode>
      <MsalProvider instance={msal}>
        <BrowserRouter>
          {/* Die Querschnittsbausteine liegen ueber der ganzen Anwendung:
              Meldungen zuerst, damit der Drawer sie nutzen kann. */}
          <Toasts>
            {/* Die Seitenaktionen liegen über dem Anlegeweg: der Seitenkopf
                fragt sie, und die Seiten darunter melden sich bei ihnen an. */}
            <Seitenaktionen>
              {/* Der Modulrahmen liegt ueber dem Anlegeweg und unter `App`:
                  `App` meldet den Zuschnitt des Mandanten an, der Drawer
                  darueber liest ihn. Ohne das wusste ein Formular nichts von
                  abgeschalteten Modulen — und zeigte bei HOWOGE ein Feld
                  „Kritikalitaet", das es dort gar nicht gibt. */}
              <Modulrahmen>
                {/* Der Standrahmen liegt ueber allem, was Daten holt: im
                    Rueckblick traegt jeder Abruf den Kopf `X-Stand`, auch
                    der aus einer Schublade. */}
                <Standrahmen>
                  <Anlegeweg>
                    <App />
                  </Anlegeweg>
                </Standrahmen>
              </Modulrahmen>
            </Seitenaktionen>
          </Toasts>
        </BrowserRouter>
      </MsalProvider>
    </StrictMode>,
  );

  /* Gleichzeitiges Arbeiten: alle 15 s fragen, was andere geändert haben —
     erst nach der Anmeldung, und nur im sichtbaren Reiter (`api/live.ts`). */
  liveStarten();
}

/** Anmeldung vorbereiten — nur im Regelbetrieb. */
async function anmeldungVorbereiten(): Promise<void> {
  await msal.initialize();

  // Nach der Rueckkehr von der Anmeldeseite steht das Ergebnis hier an
  const ergebnis = await msal.handleRedirectPromise();
  if (ergebnis?.account) {
    /* Frisch angemeldet: ab jetzt laeuft die Frist von acht Stunden. Ein
       erneuertes Token setzt sie bewusst nicht zurueck. */
    if (ergebnis.idToken && msal.getAllAccounts().length > 0 && sitzungAbgelaufen()) sitzungMerken();
    msal.setActiveAccount(ergebnis.account);
  } else {
    /* Eine Anmeldung aus einem anderen Fenster gilt — solange sie juenger
       als acht Stunden ist. */
    if (msal.getAllAccounts().length > 0 && sitzungAbgelaufen()) await sitzungBeenden();
    const vorhanden = msal.getAllAccounts()[0];
    if (vorhanden) msal.setActiveAccount(vorhanden);
  }

  msal.addEventCallback((nachricht) => {
    if (nachricht.eventType === EventType.LOGIN_SUCCESS && nachricht.payload) {
      msal.setActiveAccount((nachricht.payload as AuthenticationResult).account);
    }
  });
}

void starten();
